import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { MiBox, MiButton, MiSwitch } from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { IMaterialsResponse, IMergedBidArea } from "@miplatform/types";
import { mdiPlus } from "@mdi/js";
import { SearchResultsContainer } from "@mibuildweb/components";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import { AddAreaDrawer } from "./components/AddAreaDrawer/AddAreaDrawer";
import { BidAreaOption } from "./components/BidAreaOption/BidAreaOption";
import { DrawsSummary } from "./components/DrawsSummary/DrawsSummary";
import { MaterialResultCard } from "./components/MaterialResultCard/MaterialResultCard";
import { OptionsSection } from "./components/OptionsSection/OptionsSection";
import { CollapsibleIcon } from "../../../components/CollapsibleIcon/CollapsibleIcon";
import { CategoryFilterDrawer } from "../../../components/FilterDrawer/CategoryFilterDrawer";
import { BidDocumentViewer } from "../BidDocumentViewer/BidDocumentViewer";
import { useConfigureBidViewModel } from "../ConfigureBid/useConfigureBidViewModel";
import { useCreateBidContext } from "../CreateBidContext";
import { ReplaceItemsModal } from "../ReplaceItemsModal/ReplaceItemsModal";
import { AREA_COLUMNS } from "../constants";
import { useConfigureOptionsViewModel } from "./useConfigureOptionsViewModel";

export function ConfigureOptions() {
  const {
    openAreaDrawer,
    selectedFloor,
    addedFloorIds,
    addedFloors,
    planAreas,
    showCategoryFilter,
    selectedCategory,
    selectedAttributeValues,
    search,
    materialResults,
    openMaterialResults,
    selectedItem,
    checkedAreas,
    debouncedSearch,
    categoryFilterOptions,
    linkedMaterialDetails,
    showLinkedItems,
    collapsedFloor,
    handleCollapseFloor,
    setShowLinkedItems,
    setSearch,
    setOpenAreaDrawer,
    setSelectedFloor,
    setOpenMaterialResults,
    setSelectedItem,
    getAreaType,
    getMaterialOptions,
    getLinkedMaterials,
    handleOpenCategoryFilter,
    handleCloseCategoryFilter,
    handleCategoryClick,
    handleAttributeValueClick,
    handleSelectItem,
    handleResetEverything,
    resetMaterialDrawerStates,
  } = useConfigureBidViewModel();

  const {
    isEditingOption,
    optionData,
    setOptionData,
    selectedOption,
    setSelectedOption,
    planOptionMaterials,
    optionPricing,
    mergedAreas,
    groupedMergedAreas,
    openOptionSection,
    optionMaterialIds,
    optionMaterialIdsKey,
    builderId,
    replaceModal,
    isPreviewArea,
    setPreviewAreas,
    setOpenOptionSection,
    getOption,
    getAllOptionPricing,
    getPlanOptionMaterial,
    handleCreateOption,
    handleUpdateOption,
    handleDeleteOption,
    handleAddAreaToOption,
    handleAddItemToOption,
    handleOpenReplaceItemModal,
    handleCloseReplaceItemModal,
    handleReplaceItem,
    handleResetEverythingOption,
  } = useConfigureOptionsViewModel();

  const { classes, theme, cx } = useStyles();
  const { openDocumentViewer, setOpenDocumentViewer } = useCreateBidContext();

  useEffect(() => {
    if (!isEditingOption) {
      getAllOptionPricing();
    }
  }, [isEditingOption]);

  useEffect(() => {
    if (optionMaterialIds.length > 0) {
      getPlanOptionMaterial();
    }
  }, [optionMaterialIdsKey]);

  useEffect(() => {
    if (showLinkedItems && planOptionMaterials.length > 0) {
      getLinkedMaterials(planOptionMaterials);
    }
  }, [showLinkedItems, planOptionMaterials]);

  useEffect(() => {
    if (
      showCategoryFilter &&
      (debouncedSearch || selectedCategory?.categoryId)
    ) {
      getMaterialOptions();
    }
  }, [debouncedSearch, selectedCategory]);

  useEffect(() => {
    if (openDocumentViewer) {
      handleResetEverything();
      handleResetEverythingOption();
    } else {
      setOpenOptionSection(true);
    }
    if (!isEditingOption) {
      handleResetEverything();
    }
  }, [openDocumentViewer, isEditingOption]);

  const renderColumn = (areas: IMergedBidArea[], column: number) => {
    const columnAreas = areas.filter((a) => a.specColumn === column);
    return (
      <Box className={classes.columnBox}>
        {columnAreas.map((area) => {
          const isPreview = isPreviewArea(area);
          const areaType = getAreaType(area);

          return (
            <BidAreaOption
              key={area.id}
              area={area}
              areaType={areaType}
              planOptionMaterials={planOptionMaterials}
              isPreview={isPreview}
              isEditingOption={isEditingOption}
              checkedAreas={checkedAreas}
              showCategoryFilter={showCategoryFilter}
              openAreaDrawer={openAreaDrawer}
              showLinkedItems={showLinkedItems}
              linkedMaterialDetails={linkedMaterialDetails}
              optionData={optionData}
              setOptionData={setOptionData}
              handleOpenReplaceItemModal={handleOpenReplaceItemModal}
              handleOpenItemDrawer={() => {
                setOpenDocumentViewer(false);
                handleOpenCategoryFilter(area);
              }}
            />
          );
        })}
      </Box>
    );
  };

  return (
    <Box className={classes.root}>
      <MiBox
        headerTitle="Add Options"
        headerAction={
          <Box className={classes.headerActionBox}>
            {(planAreas?.length > 0 || addedFloorIds.length > 0) && (
              <MiSwitch
                label="Show Linked Items"
                handleChange={() => setShowLinkedItems((prev) => !prev)}
                value={showLinkedItems}
                className={classes.showLinkedSwitch}
              />
            )}
            <DrawsSummary />
          </Box>
        }
        className={classes.miBox}
        height="auto"
      >
        <Box className={classes.contentBox}>
          <Box className={classes.leftContentBox}>
            {addedFloors.map((floor) => {
              const floorAreas = groupedMergedAreas[floor.id] || [];
              const isCollapsed = collapsedFloor[floor.id];
              const isFloorSelected =
                openAreaDrawer && selectedFloor?.id === floor.id;

              return (
                <Accordion
                  expanded={!isCollapsed}
                  disableGutters
                  key={floor.id}
                  className={cx(
                    classes.accordion,
                    isFloorSelected && classes.activeBorder
                  )}
                >
                  <AccordionSummary className={classes.accordionSummary}>
                    <Box className={classes.summaryBox}>
                      <Typography fontWeight={theme.fontWeight.bold}>
                        {floor.name}
                      </Typography>
                      <Box className={classes.floorHeaderActions}>
                        {isEditingOption && (
                          <MiButton
                            title="Add Area"
                            variant={BUTTON_VARIANTS.OUTLINED}
                            size="small"
                            startIcon={{ path: mdiPlus, iconSize: 0.8 }}
                            disabled={showCategoryFilter || openMaterialResults}
                            onClick={() => {
                              setSelectedFloor(floor);
                              setOpenAreaDrawer(true);
                              setOpenDocumentViewer(false);
                            }}
                          />
                        )}
                        <CollapsibleIcon
                          isCollapsed={!isCollapsed}
                          onCollapse={() => handleCollapseFloor(floor.id)}
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    {renderColumn(floorAreas, AREA_COLUMNS.LEFT)}
                    {renderColumn(floorAreas, AREA_COLUMNS.RIGHT)}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
          <OptionsSection
            open={openOptionSection && !openMaterialResults}
            optionData={optionData}
            optionPricing={optionPricing}
            isEditingOption={isEditingOption}
            selectedOption={selectedOption}
            setOptionData={setOptionData}
            setSelectedOption={setSelectedOption}
            getOption={getOption}
            handleCreateOption={handleCreateOption}
            handleUpdateOption={handleUpdateOption}
            handleDeleteOption={handleDeleteOption}
            handleCancelOptionEdit={() => {
              handleCloseCategoryFilter();
              setOpenAreaDrawer(false);
            }}
          />
        </Box>
      </MiBox>
      <BidDocumentViewer />
      <AddAreaDrawer
        open={openAreaDrawer}
        onClose={() => {
          setOpenAreaDrawer(false);
          setPreviewAreas([]);
          setSelectedFloor(null);
        }}
        isEditingOption={isEditingOption}
        mergedAreas={mergedAreas}
        selectedFloor={selectedFloor}
        setPreviewAreas={setPreviewAreas}
        handleAddAreaToOption={handleAddAreaToOption}
      />
      <SearchResultsContainer
        open={openMaterialResults}
        flex={0.7}
        onClose={() => {
          setSelectedItem(null);
          resetMaterialDrawerStates();
          setOpenMaterialResults(false);
        }}
        alertTitle="Select Item(s) and add to Area(s)."
        alertDescription="After selecting the item, choose the Area(s) and select the Add to Area(s) button."
        storageKey="bidOptions-alert"
        results={materialResults}
        hasFilters={!!search || !!selectedCategory?.categoryId}
        handleResetFilterSelection={resetMaterialDrawerStates}
        renderResults={() =>
          materialResults.map((material: IMaterialsResponse, index: number) => (
            <MaterialResultCard
              key={material.id}
              index={index}
              material={material}
              isSelected={selectedItem?.id === material.id}
              handleSelectItem={handleSelectItem}
              checkedAreas={checkedAreas}
              handleAddItemToOption={() => {
                if (!selectedItem) return;
                checkedAreas.forEach((area) =>
                  handleAddItemToOption(area, selectedItem)
                );
                setSelectedItem(null);
              }}
            />
          ))
        }
      />
      <CategoryFilterDrawer
        open={showCategoryFilter}
        onClose={handleCloseCategoryFilter as any}
        title="Filter Items"
        categoryFilterOptions={categoryFilterOptions}
        search={search}
        selectedCategory={selectedCategory}
        selectedAttributeValues={selectedAttributeValues}
        setSearch={setSearch}
        handleCategoryClick={handleCategoryClick}
        handleAttributeValueClick={handleAttributeValueClick}
        handleReset={resetMaterialDrawerStates}
        handleResetSelection={resetMaterialDrawerStates}
      />
      <ReplaceItemsModal
        open={replaceModal.open}
        builderId={builderId}
        selectedItem={replaceModal.item}
        selectedArea={replaceModal.area}
        planAreas={mergedAreas}
        onClose={handleCloseReplaceItemModal}
        onReplaceItem={handleReplaceItem}
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    overflow: "hidden",
    height: "100%",
  },
  headerActionBox: {
    display: "flex",
    gap: theme.spacing(2),
  },
  showLinkedSwitch: {
    display: "none",
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: theme.palette.primary.light,
      opacity: 1,
    },
    "& .MuiSwitch-track": {
      backgroundColor: theme.palette.grey.medium,
      opacity: 1,
    },
    "& .MuiFormControlLabel-label": {
      color: theme.palette.common.white,
      fontSize: theme.fontSize.medium,
      fontWeight: theme.fontWeight.medium,
    },
  },
  miBox: {
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  contentBox: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    gap: 8,
  },
  leftContentBox: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    flex: 1,
    overflow: "auto",
  },
  accordion: {
    borderRadius: 4,
    boxShadow: "none",
    "&::before": { content: "none" },
  },
  activeBorder: {
    border: `2px solid ${theme.palette.primary.accent}`,
  },
  accordionSummary: {
    backgroundColor: theme.palette.primary.light,
    borderRadius: 4,
    minHeight: 36,
    "& .MuiAccordionSummary-content": {
      margin: 0,
      cursor: "default",
    },
  },
  summaryBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  floorHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  accordionDetails: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 1),
  },
  columnBox: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 400,
    gap: theme.spacing(2),
    "@container (min-width: 1200px)": {
      flexWrap: "nowrap",
    },
    "@container (max-width: 1199px)": {
      flexWrap: "wrap",
    },
  },
}));
